import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, Target, Clock, BookOpen, CheckCircle2 } from 'lucide-react'
import { api } from '../services/api'

interface QuizResult {
  resultId: string
  // Percentage 0-100 (backend now always returns this — derived from score/total for legacy rows)
  percentage: number
  // Absolute correct-answer count (0..total), kept for the "X/Y correctas" display
  score: number
  total?: number
  materialId?: string
  materialTitle?: string | null
  completedAt?: string
  timeSpent?: number
  difficulty?: string
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatTime(seconds: number) {
  if (!seconds || seconds < 0) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

// Shared color scale for percentages.
// >=80 green, 60-79 lime, 40-59 amber, <40 red.
function scoreColorFor(pct: number): string {
  if (pct >= 80) return '#10B981'
  if (pct >= 60) return '#A3E635'
  if (pct >= 40) return '#FBBF24'
  return '#EF4444'
}

/**
 * Minimal inline SVG sparkline (no external charting dep).
 * Receives oldest→newest values and renders a smooth stroke.
 */
function Sparkline({ values, color, width = 120, height = 32 }: {
  values: number[]
  color: string
  width?: number
  height?: number
}) {
  if (values.length < 2) return null
  const min = 0
  const max = 100
  const pad = 2
  const stepX = (width - pad * 2) / (values.length - 1)
  const points = values.map((v, i) => {
    const x = pad + i * stepX
    const y = pad + (1 - (v - min) / (max - min)) * (height - pad * 2)
    return [x, y] as const
  })
  const d = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ')
  const areaD = `${d} L${points[points.length - 1][0]},${height} L${points[0][0]},${height} Z`

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`spark-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#spark-${color.slice(1)})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === points.length - 1 ? 2.5 : 1.5} fill={color} />
      ))}
    </svg>
  )
}

/**
 * Full-width trend chart with x=date, y=percentage (0-100).
 * Oldest on the left, newest on the right.
 */
function TrendChart({ results }: { results: QuizResult[] }) {
  const chronological = useMemo(() => [...results].reverse(), [results])
  if (chronological.length < 2) return null

  const width = 720
  const height = 160
  const padX = 32
  const padY = 20
  const chartW = width - padX * 2
  const chartH = height - padY * 2

  const pts = chronological.map((r, i) => {
    const x = padX + (i * chartW) / (chronological.length - 1)
    const y = padY + (1 - r.percentage / 100) * chartH
    return { x, y, r }
  })

  const linePath = pts.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ')

  return (
    <div className="glass-card p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: '#E2E8F0' }}>Tendencia</h3>
        <span className="text-xs" style={{ color: '#64748B' }}>
          {chronological.length} quizzes
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 180 }}>
        {/* Horizontal gridlines at 0/40/60/80/100 */}
        {[0, 40, 60, 80, 100].map((v) => {
          const y = padY + (1 - v / 100) * chartH
          return (
            <g key={v}>
              <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
              <text x={padX - 6} y={y + 3} fontSize={9} fill="#475569" textAnchor="end">{v}</text>
            </g>
          )
        })}
        <path d={linePath} fill="none" stroke="#818CF8" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill={scoreColorFor(p.r.percentage)} stroke="#0A0F1E" strokeWidth={1.5}>
            <title>{`${Math.round(p.r.percentage)}% · ${p.r.materialTitle ?? 'Quiz'}${p.r.completedAt ? ` · ${formatDate(p.r.completedAt)}` : ''}`}</title>
          </circle>
        ))}
      </svg>
    </div>
  )
}

export default function StudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()
  const [results, setResults] = useState<QuizResult[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!studentId) return
    api.getStudentResults(studentId)
      .then((data) => setResults(data.results as QuizResult[]))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [studentId])

  const avgScore = results.length > 0
    ? Math.round(results.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0) / results.length)
    : 0
  const best = results.length > 0 ? Math.max(...results.map((r) => Number(r.percentage) || 0)) : 0
  const totalTime = results.reduce((sum, r) => sum + (Number(r.timeSpent) || 0), 0)
  const sparkValues = useMemo(
    () => [...results].reverse().map((r) => Number(r.percentage) || 0),
    [results],
  )

  return (
    <div className="max-w-3xl">
      <button
        onClick={() => navigate('/parent')}
        className="flex items-center gap-2 text-sm mb-6 transition-colors"
        style={{ color: '#64748B' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#64748B' }}
      >
        <ArrowLeft className="w-4 h-4" /> Volver al dashboard
      </button>

      <h1 className="text-2xl font-bold mb-6" style={{ color: '#F1F5F9' }}>
        Historial del estudiante
      </h1>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4" style={{ color: '#818CF8' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>Promedio</span>
          </div>
          <div className="flex items-end justify-between gap-2">
            <p className="text-3xl font-bold" style={{ color: avgScore > 0 ? scoreColorFor(avgScore) : '#F1F5F9' }}>
              {results.length > 0 ? `${avgScore}%` : '—'}
            </p>
            {sparkValues.length >= 2 && (
              <Sparkline values={sparkValues} color={scoreColorFor(avgScore)} width={90} height={28} />
            )}
          </div>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4" style={{ color: '#10B981' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>Mejor puntaje</span>
          </div>
          <p className="text-3xl font-bold" style={{ color: best > 0 ? scoreColorFor(best) : '#F1F5F9' }}>
            {results.length > 0 ? `${Math.round(best)}%` : '—'}
          </p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" style={{ color: '#A5B4FC' }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748B' }}>Tiempo total</span>
          </div>
          <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>
            {totalTime > 0 ? (totalTime >= 3600 ? `${Math.floor(totalTime / 3600)}h ${Math.floor((totalTime % 3600) / 60)}m` : `${Math.floor(totalTime / 60)}m`) : '—'}
          </p>
          <p className="text-xs mt-1" style={{ color: '#64748B' }}>{results.length} {results.length === 1 ? 'quiz' : 'quizzes'}</p>
        </div>
      </div>

      {/* Trend chart */}
      {!loading && results.length >= 2 && <TrendChart results={results} />}

      {/* Results list */}
      <div className="glass-card overflow-hidden">
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Historial de quizzes</h2>
        </div>

        {loading && (
          // Skeleton rows instead of bare "Cargando…"
          <div>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="px-6 py-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              >
                <div className="flex flex-col gap-2">
                  <div style={{ width: 180, height: 12, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ width: 120, height: 10, borderRadius: 4, background: 'rgba(255,255,255,0.04)' }} />
                </div>
                <div style={{ width: 48, height: 18, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
              </div>
            ))}
          </div>
        )}

        {!loading && results.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: '#64748B' }}>
            Este estudiante aún no ha completado ningún quiz.
          </div>
        )}

        {!loading && results.map((r) => {
          const pct = Number(r.percentage) || 0
          const pctColor = scoreColorFor(pct)
          const correct = Number(r.score) || 0
          const total = Number(r.total) || 0
          return (
            <div
              key={r.resultId}
              className="flex items-center justify-between px-6 py-4 gap-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#64748B' }} />
                  <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>
                    {r.materialTitle || 'Quiz sin título'}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#64748B' }}>
                  {r.completedAt && <span>{formatDate(r.completedAt)}</span>}
                  {r.timeSpent ? <span>· {formatTime(Number(r.timeSpent))}</span> : null}
                  {total > 0 && (
                    <span className="inline-flex items-center gap-1">
                      · <CheckCircle2 className="w-3 h-3" /> {correct}/{total}
                    </span>
                  )}
                </div>
              </div>
              <span
                className="text-lg font-bold tabular-nums"
                style={{ color: pctColor, minWidth: '3.5rem', textAlign: 'right' }}
              >
                {Math.round(pct)}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
